import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { getStorefrontConfig, isSinglePageTemplateConfig } from '../../utils/storefrontConfig';

/**
 * Route guard that redirects certain routes to home page
 * when single-page template is active
 */
export const TemplateRouteGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const [isSinglePageTemplate, setIsSinglePageTemplate] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);

  // In admin preview mode the iframe URL is the source of truth — never redirect,
  // otherwise clicking Products/Cart/Checkout tabs in the template modal bounces home.
  const isPreviewMode =
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).has('__diviPreview');

  useEffect(() => {
    if (isPreviewMode) {
      setIsSinglePageTemplate(false);
      setLoading(false);
      return;
    }
    const checkTemplate = async () => {
      try {
        // Optimization: Check for embedded config first to avoid async delay
        if (window.__STOREFRONT_CONFIG__ && window.__STOREFRONT_CONFIG__.template) {
          console.log('[TemplateRouteGuard] Using embedded config');
          const isSinglePage = isSinglePageTemplateConfig(window.__STOREFRONT_CONFIG__);
          setIsSinglePageTemplate(isSinglePage);
          setLoading(false);
          return;
        }

        const config = await getStorefrontConfig();
        console.log('[TemplateRouteGuard] Config loaded:', !!config);

        if (config) {
          const isSinglePage = isSinglePageTemplateConfig(config);
          setIsSinglePageTemplate(isSinglePage);
        } else {
          setIsSinglePageTemplate(false);
        }
      } catch (error) {
        console.error('[TemplateRouteGuard] Error checking template:', error);
        // If storefront config is not available, assume not single-page template
        setIsSinglePageTemplate(false);
      } finally {
        setLoading(false);
      }
    };

    checkTemplate();
  }, [isPreviewMode]);

  // Routes that should be blocked for single-page template
  // These routes don't exist in single-page template - everything is on the landing page
  const blockedRoutes = ['/checkout', '/cart', '/products'];
  const isBlockedRoute = blockedRoutes.some(route => {
    // Block exact matches and paths that start with these routes
    return location.pathname === route || location.pathname.startsWith(route + '/');
  });

  // Show loading state while checking
  if (loading) {
    console.log('[TemplateRouteGuard] Loading...');
    return null; // Or a loading spinner
  }

  // If single-page template and trying to access blocked route, redirect to home
  if (isSinglePageTemplate && isBlockedRoute) {
    return <Navigate to="/" replace />;
  }

  // Otherwise, render the children normally
  return <>{children}</>;
};

